// src/components/Layout.js
import React, { useContext, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { event } from '../utils/gtm';
import { useRenewalTime } from './useRenewalTime';
// Import all the components needed for the dashboard
import Sidebar from './Sidebar';
import ProfileButton from './ProfileButton';
import logo from '../img/tubetactix-logo.svg';
import FeedbackModal from './FeedbackModal';

const CreditTracker = () => {
  const { user } = useContext(UserContext);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const timezone = user?.credits_last_reset ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
  let renewalTime = '';

  renewalTime = useRenewalTime(user?.credits_last_reset, timezone);

  // The early return is now placed *after* all hook calls, fixing the ESLint error.
  if (!user) return null; // Don't show if no user

  const totalCredits = (user.free_credits || 0) + (user.subscription_credits || 0) + (user.purchased_credits || 0);

  return (
    <div className="relative">
      <button
        onClick={() => setIsTooltipOpen(!isTooltipOpen)}
        className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-2"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" height="16" viewBox="0 0 64 64" 
          role="img" aria-label="credits: stacked coins"
        >
          <defs>
            <linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fef08a"/>
              <stop offset="100%" stopColor="#facc15"/>
            </linearGradient>
            <linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#eab308"/>
              <stop offset="100%" stopColor="#ca8a04"/>
            </linearGradient>
          </defs>
      <ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2" />
          <rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
          <ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
          <rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
          <ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
          <rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/>
          <ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/>
        </svg>
    <span className="hidden sm:inline">Credits: </span>
    <span>{totalCredits}</span>
      </button>
      {isTooltipOpen && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-gray-800/70 backdrop-blur-lg border border-white/10 rounded-lg p-2 text-sm z-20">
          <div className="flex justify-between p-1"><span>Free Daily</span><span>{user.free_credits || 0}</span></div>
          <div className="flex justify-between p-1"><span>Pro Monthly</span><span>{user.subscription_credits || 0}</span></div>
          <div className="flex justify-between p-1"><span>Purchased</span><span>{user.purchased_credits || 0}</span></div>
          {renewalTime && (
            <div className="border-t border-white/10 mt-1 pt-1 text-xs text-gray-400 text-center">
              Resets at {renewalTime}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DashboardLayout = ({
  goToHome,
  handleToolChange,
  isMobileNavOpen,
  setIsMobileNavOpen,
  handleLogout,
  isFeedbackModalOpen,
  openFeedbackModal,
  closeFeedbackModal,
  onFeedbackModalClose
}) => {
  const location = useLocation();
  const { user } = useContext(UserContext); // Get user from context

  // Show sidebar only if the user is logged in and not on the homepage.
  const showSidebar = user && location.pathname !== '/';

  return (
    // This wrapper is the new flex-grow container. It will expand to fill the space
    // between the header and the footer. The main page now handles scrolling.
    <div className="flex-grow relative">
      <header className="fixed top-0 left-0 w-full h-[70px] px-10 flex items-center justify-between z-50 bg-gray-900/30 backdrop-blur-lg border-b border-white/10">
        <div className="header-left flex items-center gap-4">
          {showSidebar && (
            <button className="md:hidden" onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}>
              <svg className="w-7 h-7 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          )}
          <div className="flex items-center gap-3 cursor-pointer" onClick={goToHome}>
            <img src={logo} alt="TubeTactix Logo" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">TubeTactix</span>
          </div>
        </div>
        <div className="header-right">
          {/* Only show user-specific items if a user is logged in */}
          {user && <CreditTracker />}
          {user && <ProfileButton handleLogout={handleLogout} />}
        </div>
      </header>
      {/* This inner div no longer needs height control; its parent handles scrolling. */}
      <div className="flex pt-[70px] min-h-screen">
        {showSidebar && (
            <Sidebar 
                location={location}
                setActiveTool={handleToolChange}
                isMobileNavOpen={isMobileNavOpen}
                setIsMobileNavOpen={setIsMobileNavOpen}
            />
        )}
        {isMobileNavOpen && <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setIsMobileNavOpen(false)}></div>}
        <main className={`flex-grow w-full ${showSidebar ? 'md:pl-[70px]' : ''}`}>
          <div className="p-4 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Feedback Button and Modal (only shown for logged-in users) */}
      {user && (
        <>
          <button
            onClick={() => {
              event({ action: 'click_nav', category: 'Navigation', label: 'Feedback Button' });
              openFeedbackModal();
            }}
            className="feedback-button"
            title="Share Feedback"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
          <FeedbackModal isOpen={isFeedbackModalOpen} onClose={closeFeedbackModal} onModalClose={onFeedbackModalClose} />
        </>
      )}
    </div>
  );
};

export default DashboardLayout;